import { Mutation, Action } from 'vuex-module-decorators';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { Resolve } from 'vue-di';
import { SurveySessionValueModel } from '@trialcard/apigateway.models';
import SessionModule from './session';
import HubModule from './hub';
import { Settings } from '~/services/settings';

export interface IEligibilityQuestions {
    surveySessionId: string;
    questionId: number;
    answerId: number;
}
@InjectModule({ stateFactory: true }, module)
export default class EligibilityModule extends InjectVuexModule {
    @Resolve
    public settings!: Settings;

    @Resolve
    public session!: SessionModule;

    @Resolve
    public hub!: HubModule;

    public eligibilityData = [] as SurveySessionValueModel[];

    public isEligible = false;

    @Mutation
    public async updateEligibilityData(newEligibilityData: typeof EligibilityModule.prototype.eligibilityData) {
        this.eligibilityData = newEligibilityData;
    }

    @Mutation
    public async updateIsEligible(isEligible: boolean) {
        this.isEligible = isEligible;
    }

    @Mutation
    public clearStore() {
        this.isEligible = false;
        this.eligibilityData = [];
    }

    @Action({ rawError: true })
    public checkPatientEligibility(eligibilityData: SurveySessionValueModel[]): boolean {
        const correctAnswers = [
            +this.settings.eligibilityPatientFederalAnswer,
            +this.settings.eligibilityPatientPrescribedAnswer,
            +this.settings.eligibilityPatientInsuranceAnswer,
            +this.settings.eligibilityPatientCitizenAnswer,
            +this.settings.patientEnrollmentAnswer,
        ] as number[];

        const isEligible = eligibilityData.every(question => question.answerId && correctAnswers.includes(question.answerId));
        this.updateIsEligible(isEligible);
        return isEligible;
    }

    @Action({ rawError: true })
    public checkPharmacyEligibility(eligibilityData: SurveySessionValueModel[]): boolean {
        const correctAnswers = [
            +this.settings.eligibilityPharmacyPrescribedAnswer,
            +this.settings.eligibilityPharmacyFederalAnswer,
            +this.settings.eligibilityPharmacyInsuranceAnswer,
            +this.settings.eligibilityPharmacyCitizenAnswer,
            +this.settings.eligibilityPharmacyTermsAnswer,
            +this.settings.pharmacyEnrollmentAnswer,
        ] as number[];

        const isEligible = eligibilityData.every(question => question.answerId && correctAnswers.includes(question.answerId));
        this.updateIsEligible(isEligible);
        return isEligible;
    }

    public addAttestationToSurvey(doesAgreeMarketing: boolean | null) {
        // Only add the survey question if they answered Yes to the question
        if(doesAgreeMarketing) {
            const {
                patientDoesAgreeQuestion: patientQ,
                pharmacyDoesAgreeQuestion: pharmacyQ,
                patientDoesAgreeAnswer: patientA,
                pharmacyDoesAgreeAnswer: pharmacyA,
            } = this.settings;
            // Remove the attestation question if it's already in the array, this keeps from adding duplicates
            const eligibilityData = [ ...this.eligibilityData.filter(q => ![+patientQ, +pharmacyQ].includes(q.questionId!)) ];

            // Determine correct question and answer Id's based on path
            const isPatient = this.hub.path === 'patient';
            const questionId = isPatient ? +patientQ : +pharmacyQ;
            const answerId = isPatient ? +patientA : +pharmacyA;

            // Add the Q/A pair to the eligibilityData
            eligibilityData.push({ questionId, answerId })
            this.updateEligibilityData(eligibilityData);
        }
    }
}
